import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, TrendingUpIcon, DollarSignIcon, ShieldCheckIcon, BarChart3Icon, StarIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const comparisonData = [{
  feature: 'Focus',
  oem: 'Product adoption',
  si: 'Large-scale development',
  staffAug: 'Bodies on contract',
  nrs: 'Operational governance'
}, {
  feature: 'Engagement Model',
  oem: 'Advisory (shallow)',
  si: 'Project-based (expensive)',
  staffAug: 'Time & materials',
  nrs: 'Fractional/Retainer'
}, {
  feature: 'Decision Authority',
  oem: 'Won\'t say "no"',
  si: 'Builds what you ask',
  staffAug: 'No strategic input',
  nrs: 'Acts as "brake" & "steering"'
}, {
  feature: 'Speed',
  oem: 'Slow (standardized)',
  si: 'Slow (contract-heavy)',
  staffAug: 'Variable',
  nrs: '30-60 day sprints'
}, {
  feature: 'Cost',
  oem: 'High (Impact = 20% ACV)',
  si: 'Very high (large teams)',
  staffAug: 'Medium',
  nrs: 'Moderate (expert-led)'
}, {
  feature: 'Incentive',
  oem: 'License renewals',
  si: 'Billable hours',
  staffAug: 'Staff utilization',
  nrs: 'Your independence'
}, {
  feature: 'Federal Expertise',
  oem: 'General',
  si: 'Deep but generic',
  staffAug: 'Variable',
  nrs: 'IC/DoD specialized'
}, {
  feature: 'Outcome',
  oem: 'Feature adoption',
  si: 'Custom builds',
  staffAug: 'Capacity',
  nrs: 'Sustainable operations'
}];
export function CaseStudiesPage() {
  return <div className="w-full" data-id="element-171">
      {/* HERO */}
      <section className="bg-gradient-to-br from-nrs-navy to-nrs-blue py-16 px-4" data-id="element-172">
        <div className="max-w-7xl mx-auto text-center" data-id="element-173">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-174">
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-white mb-4" data-id="element-175">
              Case Studies
            </h1>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto" data-id="element-176">
              Real outcomes from federal agencies that engaged NRS for
              governance and platform stewardship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED CASE STUDY */}
      <section className="bg-white py-16 px-4" data-id="element-177">
        <div className="max-w-7xl mx-auto" data-id="element-178">
          <div className="inline-block px-3 py-1 bg-nrs-orange/10 border border-nrs-orange/30 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-6" data-id="element-179">
            Featured Case Study
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-180">
            {/* Left: Challenge + Approach */}
            <div className="lg:col-span-2 space-y-6" data-id="element-181">
              <div data-id="element-182">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy mb-2" data-id="element-183">
                  Federal Agency Platform Transformation
                </h2>
                <p className="text-gray-500 text-sm" data-id="element-184">
                  Anonymized — Mid-size federal agency, 2,500 users
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-185">
                <h3 className="font-heading font-bold text-base text-nrs-navy mb-3" data-id="element-186">
                  The Challenge
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-187">
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-188">
                    <span className="text-red-500 font-bold" data-id="element-189">•</span>
                    Platform: 2,500 users, ITSM + HRSD + Security Operations
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-190">
                    <span className="text-red-500 font-bold" data-id="element-191">•</span>
                    Stuck on 2-year-old version, unable to upgrade
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-192">
                    <span className="text-red-500 font-bold" data-id="element-193">•</span>
                    40+ high-severity Instance Scan findings
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-194">
                    <span className="text-red-500 font-bold" data-id="element-195">•</span>
                    No formal governance framework in place
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-196">
                    <span className="text-red-500 font-bold" data-id="element-197">•</span>
                    Mounting SI costs with no independent oversight
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-198">
                    <span className="text-red-500 font-bold" data-id="element-199">•</span>
                    Audit concerns and inability to demonstrate ROI
                  </div>
                </div>
              </div>

              <div className="bg-nrs-light rounded-xl p-6 border border-nrs-blue/20" data-id="element-200">
                <h3 className="font-heading font-bold text-base text-nrs-navy mb-4" data-id="element-201">
                  The Approach
                </h3>
                <div className="space-y-4" data-id="element-202">
                  <div className="flex gap-4" data-id="element-203">
                    <div className="flex-shrink-0 w-10 h-10 bg-nrs-blue rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-204">
                      30d
                    </div>
                    <div data-id="element-205">
                      <div className="font-heading font-semibold text-sm text-nrs-navy" data-id="element-206">
                        Phase 1: Discovery
                      </div>
                      <p className="text-gray-600 text-xs mt-1" data-id="element-207">
                        Technical debt audit, stakeholder interviews, governance
                        framework design
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4" data-id="element-208">
                    <div className="flex-shrink-0 w-10 h-10 bg-nrs-blue rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-209">
                      60d
                    </div>
                    <div data-id="element-210">
                      <div className="font-heading font-semibold text-sm text-nrs-navy" data-id="element-211">
                        Phase 2: Implementation
                      </div>
                      <p className="text-gray-600 text-xs mt-1" data-id="element-212">
                        Governance board establishment, demand scoring
                        implementation, upgrade roadmap creation
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4" data-id="element-213">
                    <div className="flex-shrink-0 w-10 h-10 bg-nrs-orange rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-214">
                      ∞
                    </div>
                    <div data-id="element-215">
                      <div className="font-heading font-semibold text-sm text-nrs-navy" data-id="element-216">
                        Phase 3: Ongoing
                      </div>
                      <p className="text-gray-600 text-xs mt-1" data-id="element-217">
                        Fractional Platform Owner retainer for continuous
                        oversight and governance
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="border-l-4 border-nrs-orange pl-5 py-2" data-id="element-218">
                <p className="text-gray-700 text-base italic leading-relaxed mb-3" data-id="element-219">
                  "NRS gave us the strategic governance we couldn't afford to
                  hire full-time. For the first time, we can confidently say
                  'no' to requests that don't serve our mission."
                </p>
                <footer className="text-gray-500 text-sm font-semibold" data-id="element-220">
                  — IT Director, Federal Agency
                </footer>
              </blockquote>
            </div>

            {/* Right: Results */}
            <div data-id="element-221">
              <div className="bg-nrs-navy rounded-xl p-6 sticky top-24" data-id="element-222">
                <h3 className="font-heading font-bold text-white text-base mb-5" data-id="element-223">
                  The Results
                </h3>
                <div className="space-y-4" data-id="element-224">
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-225">
                    <div className="flex items-center gap-2 mb-1" data-id="element-226">
                      <TrendingUpIcon className="h-4 w-4 text-nrs-orange" data-id="element-227" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-228">
                        Upgrade Readiness
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-2xl" data-id="element-229">
                      58 → 91
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-230">
                      Instance Scan score improvement
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-231">
                    <div className="flex items-center gap-2 mb-1" data-id="element-232">
                      <DollarSignIcon className="h-4 w-4 text-nrs-orange" data-id="element-233" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-234">
                        Cost Optimization
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-2xl" data-id="element-235">
                      $45K
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-236">
                      Unnecessary license costs identified
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-237">
                    <div className="flex items-center gap-2 mb-1" data-id="element-238">
                      <BarChart3Icon className="h-4 w-4 text-nrs-orange" data-id="element-239" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-240">
                        Demand Management
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-2xl" data-id="element-241">
                      28%
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-242">
                      Backlog requests rejected as low-value
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-243">
                    <div className="flex items-center gap-2 mb-1" data-id="element-244">
                      <ShieldCheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-245" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-246">
                        Governance Maturity
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-lg leading-tight" data-id="element-247">
                      Functioning Board
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-248">
                      Making objective decisions monthly
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-249">
                    <div className="flex items-center gap-2 mb-1" data-id="element-250">
                      <StarIcon className="h-4 w-4 text-nrs-orange" data-id="element-251" />
                      <span className="text-nrs-orange font-heading font-bold text-sm" data-id="element-252">
                        Platform ROI
                      </span>
                    </div>
                    <div className="text-white font-heading font-bold text-2xl" data-id="element-253">
                      2.1:1
                    </div>
                    <div className="text-blue-200 text-xs" data-id="element-254">
                      Value-to-cost ratio demonstrated
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ADDITIONAL CASE STUDIES */}
      <section className="bg-nrs-light py-16 px-4" data-id="element-255">
        <div className="max-w-7xl mx-auto" data-id="element-256">
          <h2 className="font-heading font-bold text-2xl text-nrs-navy mb-8" data-id="element-257">
            Additional Engagements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-258">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-259">
              <div className="flex items-center gap-3 mb-4" data-id="element-260">
                <div className="w-10 h-10 bg-nrs-navy rounded-lg flex items-center justify-center" data-id="element-261">
                  <ShieldCheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-262" />
                </div>
                <div data-id="element-263">
                  <div className="font-heading font-bold text-nrs-navy text-base" data-id="element-264">
                    Defense Intelligence Agency (DIA)
                  </div>
                  <div className="text-gray-500 text-xs" data-id="element-265">
                    Intelligence Community | NIPR & JWICS
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-266">
                Cross-domain platform governance across NIPR and JWICS
                environments. Established data governance framework, implemented
                automated sync patterns, and achieved ATO approval for
                cross-domain data synchronization.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-267">
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-268">
                  NIPR/JWICS
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-269">
                  Data Governance
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-270">
                  ATO Compliance
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-271">
                  TS/SCI
                </span>
              </div>
            </motion.div>

            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-272">
              <div className="flex items-center gap-3 mb-4" data-id="element-273">
                <div className="w-10 h-10 bg-nrs-navy rounded-lg flex items-center justify-center" data-id="element-274">
                  <BarChart3Icon className="h-5 w-5 text-nrs-orange" data-id="element-275" />
                </div>
                <div data-id="element-276">
                  <div className="font-heading font-bold text-nrs-navy text-base" data-id="element-277">
                    Army War College
                  </div>
                  <div className="text-gray-500 text-xs" data-id="element-278">
                    Defense Education | Platform Stewardship
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-279">
                Academic institution platform stewardship program. Developed
                governance framework tailored for educational environment,
                conducted application rationalization across 80+ systems, and
                established Fractional Platform Owner model.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-280">
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-281">
                  App Rationalization
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-282">
                  Governance Framework
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-283">
                  HRSD
                </span>
                <span className="px-2 py-0.5 bg-nrs-light text-nrs-blue text-xs font-semibold rounded-full" data-id="element-284">
                  ITSM
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="bg-white py-16 px-4" data-id="element-285">
        <div className="max-w-7xl mx-auto" data-id="element-286">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-287">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-nrs-navy mb-3" data-id="element-288">
              How NRS Stacks Up
            </h2>
            <p className="text-gray-600" data-id="element-289">
              An honest comparison of how NRS differs from other ServiceNow
              partners
            </p>
          </motion.div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm" data-id="element-290">
            <table className="w-full text-sm min-w-[700px]" data-id="element-291">
              <thead data-id="element-292">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-293">
                  <th className="px-4 py-4 text-left font-heading font-semibold text-gray-700 text-xs uppercase w-32" data-id="element-294">
                    Feature
                  </th>
                  <th className="px-4 py-4 text-left font-heading font-semibold text-gray-500 text-xs uppercase" data-id="element-295">
                    ServiceNow OEM
                  </th>
                  <th className="px-4 py-4 text-left font-heading font-semibold text-gray-500 text-xs uppercase" data-id="element-296">
                    Large SIs
                  </th>
                  <th className="px-4 py-4 text-left font-heading font-semibold text-gray-500 text-xs uppercase" data-id="element-297">
                    Staff Aug
                  </th>
                  <th className="px-4 py-4 text-left font-heading font-bold text-nrs-blue text-xs uppercase bg-nrs-light" data-id="element-298">
                    NRS
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-299">
                {comparisonData.map((row, i) => <tr key={row.feature} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`} data-id="element-300">
                    <td className="px-4 py-3 font-heading font-semibold text-gray-700 text-xs" data-id="element-301">
                      {row.feature}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs" data-id="element-302">
                      {row.oem}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs" data-id="element-303">
                      {row.si}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs" data-id="element-304">
                      {row.staffAug}
                    </td>
                    <td className="px-4 py-3 text-nrs-blue font-semibold text-xs bg-nrs-light/50" data-id="element-305">
                      {row.nrs}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-nrs-navy py-12 px-4" data-id="element-306">
        <div className="max-w-4xl mx-auto text-center" data-id="element-307">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4" data-id="element-308">
            Ready to See Results Like These?
          </h2>
          <p className="text-blue-200 mb-6" data-id="element-309">
            Schedule a free platform assessment and let's discuss what
            governance can do for your agency.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-310">
            <a href="mailto:info@newriversystems.com" className="px-8 py-3 bg-nrs-orange text-white font-semibold rounded-md hover:bg-amber-500 transition-colors" data-id="element-311">
              Schedule Free Assessment
            </a>
            <a href="#" className="px-8 py-3 border-2 border-white text-white font-semibold rounded-md hover:bg-white hover:text-nrs-navy transition-colors" data-id="element-312">
              Download Case Study PDF
            </a>
          </div>
        </div>
      </section>
    </div>;
}